import React, { useEffect, useRef, useState } from 'react';
import { FiDownload, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { downloadElementPdf } from '../../../utils/downloadElementPdf';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import { formatProposalDate, getStatusMeta, proposalTermSections } from '../utils/proposalUtils';
import { mergeTermRows, termRowsFromCatalog } from '../../ProfilePages/components/quoteCatalog';
import { fetchCatalogServices } from '../../../utils/catalogPicks';
import '../styles/ProposalViewModal.css';

const PROPOSAL_FIELDS = `
  id
  proposalNumber
  title
  bookingId
  clientName
  clientEmail
  companyName
  siteAddress
  contactPerson
  phoneNumber
  value
  serviceCost
  gstAmount
  additionalCharges
  discount
  status
  serviceType
  frequency
  siteSize
  staffRequired
  scopeOfWork
  services
  terms
  version
  proposalDate
  expectedStartDate
  sentAt
  viewedAt
  signedAt
  signedName
  signedPosition
  signatureImage
  signatureText
  rejectReason
  expiresAt
  createdAt
`;

const FETCH_PROPOSAL = `
  query FindOneProposal($id: String!) {
    findOneProposal(id: $id) {
      ${PROPOSAL_FIELDS}
    }
  }
`;

const GET_PROPOSAL_COMPANY = `
  query ProposalCompanyProfile {
    getMyProfile {
      company {
        companyName
        tradingName
        abn
        businessNumber
        companyEmail
        invoicingEmail
        companyPhone
        companyMobile
        unitNumber
        address
        suburb
        state
        postCode
        country
      }
    }
  }
`;

const FIND_PROPOSAL_VERSIONS = `
  query FindProposalVersions($proposalNumber: String!) {
    findProposalVersions(proposalNumber: $proposalNumber) {
      ${PROPOSAL_FIELDS}
    }
  }
`;

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function display(value) {
  const text = String(value ?? '').trim();
  return text || '—';
}

function rowsFromTermText(text) {
  return proposalTermSections(text).flatMap((section, sectionIndex) => {
    const sectionKey = {
      'Service Terms': 'serviceTerms',
      'Client Responsibilities': 'responsibilities',
      'Service Exclusions': 'exclusions',
      'Scope-Specific Notes': 'notes',
    }[section.title] || 'serviceTerms';
    if (section.kind === 'pairs' || sectionKey === 'serviceTerms') {
      return (section.rows || []).map((row, index) => ({
        key: `text-${sectionIndex}-${index}`,
        section: 'serviceTerms',
        title: typeof row === 'string' ? '' : row.title || '',
        body: typeof row === 'string' ? row : row.body || '',
        text: '',
        selected: true,
      }));
    }
    return (section.rows || []).map((line, index) => ({
      key: `text-${sectionIndex}-${index}`,
      section: sectionKey,
      title: '',
      body: '',
      text: line,
      selected: true,
    }));
  });
}

function sectionsFromTermRows(rows) {
  const list = mergeTermRows(Array.isArray(rows) ? rows : [], []).filter(
    (row) => row && row.selected !== false,
  );
  const text = (row) => String(row?.text || '').trim();
  return [
    {
      title: 'Service Terms',
      kind: 'pairs',
      rows: list
        .filter((row) => row.section === 'serviceTerms')
        .map((row) => ({
          title: String(row.title || '').trim(),
          body: String(row.body || '').trim(),
        }))
        .filter((row) => row.title || row.body),
    },
    {
      title: 'Client Responsibilities',
      kind: 'lines',
      rows: list.filter((row) => row.section === 'responsibilities').map(text).filter(Boolean),
    },
    {
      title: 'Service Exclusions',
      kind: 'lines',
      rows: list.filter((row) => row.section === 'exclusions').map(text).filter(Boolean),
    },
    {
      title: 'Scope-Specific Notes',
      kind: 'lines',
      rows: list.filter((row) => row.section === 'notes').map(text).filter(Boolean),
    },
  ].filter((section) => section.rows.length);
}

function companyAddress(company) {
  if (!company) return '';
  return [
    company.unitNumber,
    company.address,
    company.suburb,
    company.state,
    company.postCode,
    company.country,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');
}

function clientResponse(row) {
  const api = String(row?.status || '').toUpperCase();
  if (api === 'SIGNED') {
    return {
      tone: 'accepted',
      title: 'Accepted',
      heading: 'Accepted by',
      body: row.signedName || 'Client accepted this revision',
      meta: row.signedAt ? `Accepted at ${formatDateTime(row.signedAt)}` : '',
    };
  }
  if (api === 'REVISION') {
    return {
      tone: 'change',
      title: 'Change requested',
      heading: 'Client request',
      body: row.rejectReason || 'The client asked for changes.',
      meta: '',
    };
  }
  if (api === 'DECLINED') {
    return {
      tone: 'rejected',
      title: 'Declined',
      heading: 'Decline reason',
      body: row.rejectReason || 'The client declined this proposal.',
      meta: '',
    };
  }
  if (api === 'SENT' || api === 'VIEWED') {
    return {
      tone: 'pending',
      title: api === 'VIEWED' ? 'Viewed' : 'Awaiting client',
      heading: '',
      body:
        api === 'VIEWED'
          ? 'The client opened the proposal. No response yet.'
          : 'Sent for review. No client response yet.',
      meta: row.viewedAt
        ? `Viewed at ${formatDateTime(row.viewedAt)}`
        : row.sentAt
          ? `Sent at ${formatDateTime(row.sentAt)}`
          : '',
    };
  }
  if (api === 'EXPIRED') {
    return {
      tone: 'rejected',
      title: 'Expired',
      heading: '',
      body: 'This proposal is past its validity date.',
      meta: '',
    };
  }
  return {
    tone: 'pending',
    title: 'Draft',
    heading: '',
    body: 'This revision has not been sent to the client.',
    meta: '',
  };
}

function Detail({ label, children, full }) {
  return (
    <div className={full ? 'prop-detail prop-detail--full' : 'prop-detail'}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="prop-detail-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

export default function ProposalViewModal({ proposalId, onClose, autoDownload = false, onPdf }) {
  const docRef = useRef(null);
  const autoDone = useRef(false);
  const [downloading, setDownloading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedId, setSelectedId] = useState(proposalId || '');
  const [company, setCompany] = useState(null);
  const [catalogTerms, setCatalogTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setSelectedId(proposalId || '');
  }, [proposalId]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    graphqlRequest(GET_PROPOSAL_COMPANY)
      .then((res) => {
        if (!cancelled) setCompany(res?.getMyProfile?.company || null);
      })
      .catch(() => {
        if (!cancelled) setCompany(null);
      });
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  useEffect(() => {
    if (!proposalId) {
      setVersions([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    graphqlRequest(FETCH_PROPOSAL, { id: proposalId })
      .then(async (res) => {
        if (cancelled) return;
        const row = res?.findOneProposal;
        if (!row?.id) {
          setVersions([]);
          setError('Proposal not found.');
          return;
        }
        let rows = [row];
        if (row.proposalNumber) {
          try {
            const versionRes = await graphqlRequest(FIND_PROPOSAL_VERSIONS, {
              proposalNumber: row.proposalNumber,
            });
            const family = Array.isArray(versionRes?.findProposalVersions)
              ? versionRes.findProposalVersions
              : [];
            if (family.length) rows = family;
          } catch (err) {
            console.error('Failed to load proposal versions:', err);
          }
        }
        setVersions(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load proposal details.');
        setVersions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  const selectedVersion =
    versions.find((row) => row.id === selectedId) || versions[0] || null;
  const row = selectedVersion || { status: 'DRAFT' };
  const storedTermRows = (Array.isArray(row.services) ? row.services : []).flatMap((line) =>
    Array.isArray(line?.sourceTerms) ? line.sourceTerms : [],
  );
  const catalogKey = (Array.isArray(row.services) ? row.services : [])
    .map((line) => line?.catalogServiceId || '')
    .filter(Boolean)
    .join('|');

  useEffect(() => {
    if (storedTermRows.length || !catalogKey) {
      setCatalogTerms([]);
      return undefined;
    }
    const ids = new Set(catalogKey.split('|'));
    let cancelled = false;
    fetchCatalogServices()
      .then((list) => {
        if (cancelled) return;
        const rows = (list || [])
          .filter((service) => ids.has(service.id))
          .reduce((acc, service) => mergeTermRows(acc, termRowsFromCatalog(service)), []);
        setCatalogTerms(rows);
      })
      .catch(() => {
        if (!cancelled) setCatalogTerms([]);
      });
    return () => {
      cancelled = true;
    };
  }, [row?.id, catalogKey, storedTermRows.length]);
  const statusMeta = getStatusMeta(row.status);
  const displayVersion = Number(row.version) || 1;
  const response = clientResponse(row);

  const pdfName = `Proposal-${row.proposalNumber || 'proposal'}-v${displayVersion}.pdf`;
  const downloadPdf = async (asBlob = false) => {
    setDownloading(true);
    try {
      const blob = await downloadElementPdf(docRef.current, pdfName, {
        output: asBlob === true ? 'blob' : 'save',
      });
      if (asBlob === true) await onPdf?.(blob, pdfName);
      return true;
    } catch (err) {
      console.error('Proposal PDF failed:', err);
      toast.error('Could not download the proposal PDF.');
      return false;
    } finally {
      setDownloading(false);
    }
  };

  // "PDF" from a document list: download as soon as the content is ready.
  useEffect(() => {
    if (!autoDownload || autoDone.current || loading || error || !row?.id) return;
    autoDone.current = true;
    void downloadPdf(Boolean(onPdf)).then(() => onClose());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDownload, loading, error, row?.id]);

  return (
    <div className="prop-view-overlay" onClick={onClose} role="presentation">
      <div
        ref={docRef}
        className="prop-view-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="View proposal details"
      >
        <header className="prop-view-header">
          <div className="prop-view-title">
            <div>
              <h2>Proposal details</h2>
              <p>
                {row.proposalNumber || 'Proposal'}
                {` · v${displayVersion}`}
              </p>
            </div>
          </div>
          <div className="prop-view-header-actions" data-pdf-ignore>
            <button
              type="button"
              className="prop-view-pdf"
              onClick={() => downloadPdf()}
              disabled={loading || Boolean(error) || downloading}
            >
              <FiDownload size={15} /> {downloading ? 'Preparing…' : 'Download PDF'}
            </button>
            {versions.length > 1 ? (
              <label className="prop-view-version">
                <span>Version</span>
                <select
                  value={selectedVersion?.id || selectedId}
                  onChange={(event) => setSelectedId(event.target.value)}
                >
                  {versions.map((item) => (
                    <option key={item.id} value={item.id}>
                      v{Number(item.version) || 1} ·{' '}
                      {getStatusMeta(item.status).label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {row.status ? (
              <span className={`prop-status ${statusMeta.className}`}>
                {statusMeta.label}
              </span>
            ) : null}
            <button
              type="button"
              className="prop-view-close"
              onClick={onClose}
              aria-label="Close"
            >
              <FiX size={22} />
            </button>
          </div>
        </header>

        <div className="prop-view-body">
          {loading ? (
            <p className="prop-view-message">Loading proposal details…</p>
          ) : error ? (
            <p className="prop-view-message prop-view-message--error">{error}</p>
          ) : (
            <>
              <aside
                className={`prop-view-response prop-view-response--${response.tone}`}
                aria-label="Client response"
              >
                <p className="prop-view-response-kicker">Client response</p>
                <strong className="prop-view-response-title">{response.title}</strong>
                {response.meta ? (
                  <p className="prop-view-response-meta">{response.meta}</p>
                ) : null}
                {response.heading ? (
                  <p className="prop-view-response-heading">{response.heading}</p>
                ) : null}
                <p className="prop-view-response-copy">{response.body}</p>
                {row.signedPosition ? (
                  <p className="prop-view-response-meta">{row.signedPosition}</p>
                ) : null}
                {String(row.signatureImage || '').startsWith('data:image/') ? (
                  <img
                    className="prop-view-response-sig"
                    src={row.signatureImage}
                    alt="Client signature"
                  />
                ) : row.signatureText ? (
                  <p className="prop-view-response-sig-text">{row.signatureText}</p>
                ) : null}
              </aside>

              <Block title="Proposal">
                <dl className="prop-view-meta-grid">
                  <Detail label="Proposal number">{display(row.proposalNumber)}</Detail>
                  <Detail label="Revision">v{displayVersion}</Detail>
                  <Detail label="Title">{display(row.title)}</Detail>
                  <Detail label="Proposal date">
                    {formatProposalDate(row.proposalDate || row.createdAt) || '—'}
                  </Detail>
                  <Detail label="Expected start">
                    {formatProposalDate(row.expectedStartDate) || '—'}
                  </Detail>
                  <Detail label="Valid until">
                    {formatProposalDate(row.expiresAt) || '—'}
                  </Detail>
                </dl>
              </Block>

              <Block title="Company and client">
                <div className="prop-view-parties">
                  <div>
                    <h4>Company</h4>
                    <p><strong>{display(company?.companyName || company?.tradingName)}</strong></p>
                    <p>ABN: {display(company?.abn || company?.businessNumber)}</p>
                    <p>Address: {display(companyAddress(company))}</p>
                    <p>Phone: {display(company?.companyPhone || company?.companyMobile)}</p>
                    <p>Email: {display(company?.invoicingEmail || company?.companyEmail)}</p>
                  </div>
                  <div>
                    <h4>Client</h4>
                    <p><strong>{display(row.clientName)}</strong></p>
                    {row.companyName ? <p>Company: {display(row.companyName)}</p> : null}
                    <p>Contact: {display(row.contactPerson)}</p>
                    <p>Phone: {display(row.phoneNumber)}</p>
                    <p>Email: {display(row.clientEmail)}</p>
                    <p>Site: {display(row.siteAddress)}</p>
                  </div>
                </div>
              </Block>

              <Block title="Service">
                <dl className="prop-view-meta-grid">
                  <Detail label="Service type">{display(row.serviceType)}</Detail>
                  <Detail label="Frequency">{display(row.frequency)}</Detail>
                  <Detail label="Site size">{display(row.siteSize)}</Detail>
                  <Detail label="Staff required">
                    {row.staffRequired != null && row.staffRequired !== ''
                      ? String(row.staffRequired)
                      : '—'}
                  </Detail>
                </dl>
              </Block>

              <Block title="Pricing">
                <dl className="prop-view-meta-grid">
                  <Detail label="Service cost">
                    {formatAUD(Number(row.serviceCost) || 0)}
                  </Detail>
                  <Detail label="GST">{formatAUD(Number(row.gstAmount) || 0)}</Detail>
                  <Detail label="Additional charges">
                    {formatAUD(Number(row.additionalCharges) || 0)}
                  </Detail>
                  <Detail label="Discount">{formatAUD(Number(row.discount) || 0)}</Detail>
                  <Detail label="Total">{formatAUD(Number(row.value) || 0)}</Detail>
                </dl>
              </Block>

              {Array.isArray(row.services) && row.services.length ? (
                <Block title="Services">
                  {row.services.map((line, index) => (
                    <div key={`${line.catalogServiceId || line.name}-${index}`} className="prop-view-service">
                      <strong>{display(line.name)}</strong>
                      {line.description ? <p className="prop-detail-text">{line.description}</p> : null}
                      {(line.groups || []).map((group) => (
                        <div key={group.id || group.name}>
                          <p className="prop-view-group">{group.name || 'Tasks'}</p>
                          <table className="prop-view-tasks">
                            <thead>
                              <tr>
                                <th>Task</th>
                                <th>Client-facing scope</th>
                                <th>Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(group.tasks || []).map((task) => (
                                <tr key={task.id || task.title}>
                                  <td>{display(task.title)}</td>
                                  <td>{display(task.scope)}</td>
                                  <td>{formatAUD(Number(task.price) || 0)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  ))}
                </Block>
              ) : null}

              <Block title="Scope of work">
                <p className="prop-detail-text">{display(row.scopeOfWork)}</p>
              </Block>
              <Block title="Terms">
                {(() => {
                  const base = storedTermRows.length ? storedTermRows : catalogTerms;
                  const sections = sectionsFromTermRows(
                    base.length ? base : rowsFromTermText(row.terms),
                  );
                  const shown = sections;
                  if (!shown.length) return <p className="prop-detail-text">No terms recorded.</p>;
                  return (
                    <div className="prop-view-terms">
                      {shown.map((section) => (
                        <article key={section.title}>
                          <h5>{section.title}</h5>
                          {section.kind === 'pairs' ? (
                            section.rows.map((term, index) => (
                              <div className="prop-view-term-pair" key={`${section.title}-${index}`}>
                                {term.title ? <strong>{term.title}</strong> : null}
                                {term.body ? <p>{term.body}</p> : null}
                              </div>
                            ))
                          ) : (
                            <ul>
                              {section.rows.map((line, index) => (
                                <li key={`${section.title}-${index}`}>{line}</li>
                              ))}
                            </ul>
                          )}
                        </article>
                      ))}
                    </div>
                  );
                })()}
              </Block>
            </>
          )}
        </div>

        <footer className="prop-view-footer" data-pdf-ignore>
          <button type="button" className="prop-view-close-btn" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
